import React, { useEffect, useState } from 'react';
import { api, uploadWithFormData } from '../api';

export function BlacklistPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [phone, setPhone] = useState('');
  const [search, setSearch] = useState('');
  const [phoneColumn, setPhoneColumn] = useState(1);
  const [hasHeader, setHasHeader] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');


  function formatDate(value) {
    if (!value) return '-';
    const date = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  function handleSearch() {
    setPage(1);
    load(search, 1, pageSize).catch((currentError) => setError(currentError.message || 'Не удалось выполнить поиск'));
  }

  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }

  function handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0] || null;
    if (droppedFile) {
      setFile(droppedFile);
    }
  }

  async function load(nextSearch = search, nextPage = page, nextPageSize = pageSize) {
    setLoading(true);
    try {
      const result = await api(
        `/blacklist?search=${encodeURIComponent(nextSearch)}&page=${encodeURIComponent(nextPage)}&pageSize=${encodeURIComponent(nextPageSize)}`
      );
      setRows(Array.isArray(result?.items) ? result.items : []);
      setTotal(Number(result?.total || 0));
      setPage(Number(result?.page || nextPage || 1));
      setPageSize(Number(result?.pageSize || nextPageSize || 50));
      setTotalPages(Number(result?.totalPages || 1));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load().catch((currentError) => setError(currentError.message || 'Не удалось загрузить черный список'));
  }, []);

  async function add() {
    setError('');
    setMessage('');
    setSubmitting(true);
    try {
      await api('/blacklist/manual', { method: 'POST', body: JSON.stringify({ phone }) });
      setPhone('');
      await load(search, 1, pageSize);
      setMessage('Телефон добавлен в черный список');
    } finally {
      setSubmitting(false);
    }
  }

  async function importFile() {
    if (!file) {
      setError('Выберите файл для импорта');
      return;
    }
    setError('');
    setMessage('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('phoneColumn', String(phoneColumn || 1));
      fd.append('hasHeader', hasHeader ? 'true' : 'false');
      const result = await uploadWithFormData('/blacklist/import', fd, { timeoutMs: 600000 });
      setFile(null);
      setMessage(`Импорт завершён. Добавлено: ${result.imported || 0}`);
      await load(search, 1, pageSize);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid two-columns">
      {message ? <section className="card full-width success-banner">{message}</section> : null}
      {error ? <section className="card full-width error-banner">{error}</section> : null}
      <section className="card">
        <h2>Добавить вручную</h2>
        <label>Телефон<input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="79999999999" /></label>
        <button type="button" disabled={submitting} onClick={() => add().catch((currentError) => setError(currentError.message || 'Не удалось добавить телефон'))}>{submitting ? 'Сохранение...' : 'Добавить'}</button>
      </section>
      <section className="card">
        <h2>Импорт из файла</h2>
        <label>Колонка с телефоном
          <input type="number" className="blocklist-number" min="1" value={phoneColumn} onChange={(e) => setPhoneColumn(Number(e.target.value) || 1)} />
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
          В файле есть строка заголовков
        </label>
        <label
          className={`dropzone${dragActive ? ' active' : ''}${file ? ' has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            key={file ? file.name : 'empty'}
            type="file"
            className="dropzone-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <div className="dropzone-title">
            {file ? 'Файл выбран' : 'Перетащите файл сюда'}
          </div>
          <div className="dropzone-hint">
            {file ? file.name : 'или нажмите, чтобы выбрать файл'}
          </div>
        </label>
        <button type="button" className="blocklist-import_file-button" disabled={submitting || !file} onClick={() => importFile().catch((currentError) => setError(currentError.message || 'Не удалось импортировать файл'))}>{submitting ? 'Импорт...' : 'Импортировать'}</button>
      </section>
      <section className="card full-width">
        <div className="section-header">
          <h2>Черный список</h2>
          <div className="inline-form compact">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по телефону" />
            <button type="button" onClick={handleSearch}>Найти</button>
          </div>
        </div>
        {loading ? <div className="muted">Загрузка...</div> : null}
        {!loading ? <div className="muted blacklist-summary">Всего записей: {total}</div> : null}
        <table className="table">
          <thead><tr><th>ID</th><th>Телефон</th><th>Источник</th><th>Дата</th></tr></thead>
          <tbody>
            {rows.map((item) => <tr key={item.id}><td>{item.id}</td><td>{item.phone}</td><td>{item.source}</td><td>{formatDate(item.created_at)}</td></tr>)}
            {!rows.length && !loading ? <tr><td colSpan="4">Черный список пока пуст</td></tr> : null}
          </tbody>
        </table>
        <div className="pagination">
          <div className="pagination-controls">
            <button type="button" disabled={loading || page <= 1} onClick={() => load(search, page - 1, pageSize).catch((currentError) => setError(currentError.message || 'Не удалось загрузить страницу'))}>Назад</button>
            <span className="pagination-info">Страница {page} из {totalPages}</span>
            <button type="button" disabled={loading || page >= totalPages} onClick={() => load(search, page + 1, pageSize).catch((currentError) => setError(currentError.message || 'Не удалось загрузить страницу'))}>Вперёд</button>
          </div>
          <label className="pagination-size">
            По:
            <select
              value={pageSize}
              onChange={(e) => {
                const nextPageSize = Number(e.target.value) || 50;
                setPage(1);
                load(search, 1, nextPageSize).catch((currentError) => setError(currentError.message || 'Не удалось изменить размер страницы'));
              }}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </label>
        </div>
      </section>
    </div>
  );
}
